import { useState } from "react";
import { Typography, Card, CardContent, Snackbar, Alert } from "@mui/material";
import { Container, Box, Button } from "@/components/atoms";
import { useMembershipTypes, useCreateStripeCheckoutSession } from "@/hooks";
import { useUserContext } from "@/providers/contexts";
import { LoadingState, ErrorState } from "@/components/views";
import { MembershipType } from "@/types/user/membership_types.types";
import { useNavigate } from "react-router-dom";

const membershipFeatures: Record<string, string[]> = {
  basic: [
    "Limit to 1 page of search results",
    "Limited filters (company, healthcare domain, job setting, and date only)",
  ],
  pro: [
    "Includes basic features, but access to all results",
    "Can participate in surveys to drive new features and product map",
  ],
  premium: [
    "Includes all pro features",
    "Can save jobs",
    "Can save companies",
    "Access to all filters",
    "Access to companies directory",
    "Limited ads",
    "Early access to new features (like the Pathfinder tool)",
  ],
};

export const MembershipsPage = () => {
  const {
    data: membershipTypesData,
    loading: membershipTypesLoading,
    error: membershipTypesError,
  } = useMembershipTypes();

  const { user, isAuthenticated } = useUserContext();
  const navigate = useNavigate();
  const {
    createCheckoutSession,
    loading: stripeLoading,
    error: stripeError,
  } = useCreateStripeCheckoutSession();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const loading = membershipTypesLoading || stripeLoading;
  const error = membershipTypesError || stripeError;
  const membershipType = user?.membership_type?.name;

  const disableButton = (membershipName: MembershipType["name"]) => {
    if (!isAuthenticated) {
      return false;
    }
    return membershipType?.toLowerCase() === membershipName.toLowerCase();
  };

  const handleMembershipChange = async (membership: MembershipType) => {
    if (!user || !user.membership_type) {
      setSnackbarMessage("Please register or log in first");
      setSnackbarOpen(true);

      setTimeout(() => {
        navigate("/register");
      }, 2000);
      return;
    }
    localStorage.setItem("prevMembershipName", user.membership_type.name);

    try {
      await createCheckoutSession({ new_membership_name: membership.name });
    } catch (err) {
      console.error("Failed to start Stripe Checkout:", err);
    }
  };

  return (
    <Container componentName={`memberships-page`}>
      <Box
        componentName={`memberships-parent`}
        sx={{
          display: "flex",
          flexDirection: "column",
          m: 2,
        }}
      >
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="warning"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Typography
          variant="h3"
          align="center"
          color="primary.dark"
          sx={{
            my: 1,
            lineHeight: 2,
            textTransform: "capitalize",
            letterSpacing: 2,
            wordSpacing: 2,
          }}
        >
          Memberships
        </Typography>
        <Box
          componentName={`memberships-page`}
          border={false}
          sx={{
            display: "flex",
            flexDirection: "row",
            columnGap: 2,
            m: 2,
          }}
        >
          {error ? (
            <ErrorState errors={error} />
          ) : loading ? (
            <LoadingState />
          ) : (
            membershipTypesData?.map((membership: MembershipType) => (
              <Card
                key={membership.id}
                data-testid={`membership-card-${membership.name.toLowerCase()}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderRadius: "2px",
                  boxShadow: "3px 3px 0px 0px #000000",
                  border: "1px solid #226f54",
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    color="primary"
                    align="center"
                    gutterBottom
                  >
                    {membership.name}
                  </Typography>

                  <Box
                    componentName="membership-details"
                    border={false}
                    component="ul"
                    sx={{ pl: 2, mb: 2 }}
                  >
                    {membershipFeatures[membership.name]?.map(
                      (feature, idx) => (
                        <li key={idx}>
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                              m: 1,
                            }}
                          >
                            {feature}
                          </Typography>
                        </li>
                      ),
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.primary"
                    align="center"
                    sx={{ fontWeight: "bold" }}
                  >
                    Price: ${membership.price}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    color="text.primary"
                  >
                    Duration: {membership.duration} days
                  </Typography>
                  <Button
                    componentName={`select-${membership.name}`}
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handleMembershipChange(membership)}
                    disabled={stripeLoading || disableButton(membership.name)}
                    helperText={
                      disableButton(membership.name)
                        ? `You are already on the ${membership.name} plan`
                        : ""
                    }
                    sx={{ mt: 2 }}
                  >
                    Select {membership.name}
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Box>
    </Container>
  );
};
